import React from 'react';
import {ActiveSubscription, SeatType, SeatUsage} from '../../types/graphql';
import {
  ADDON_LABELS,
  DEFAULT_SEAT_TYPE,
  LIGHTBOX_SEAT_TYPE,
  formatLimit,
  isCwayTrial,
  isInvoiced,
  MODULE_LABELS,
  PAST_DUE_STATUS,
  paymentTermsLabel,
  seatNoun,
  subscriptionStatusLabel,
  TIER_LABELS,
  UNLIMITED,
} from '../../utils/subscriptions';

export type PillTone = 'green' | 'amber' | 'purple' | 'gray' | 'red' | 'blue' | 'teal';

const PILL_TONES: Record<PillTone, string> = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  teal: 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
};

/** Rounded status pill. `square` gives the tighter chip used for plan contents. */
export function Pill({
  tone,
  title,
  square = false,
  children,
}: {
  tone: PillTone;
  title?: string;
  square?: boolean;
  children: React.ReactNode;
}) {
  return (
    <span
      title={title}
      className={`${square ? 'px-2 py-0.5 rounded' : 'px-2.5 py-0.5 rounded-full'} text-xs font-medium whitespace-nowrap ${
        PILL_TONES[tone]
      }`}
    >
      {children}
    </span>
  );
}

type SubscriptionLike = Pick<
  ActiveSubscription,
  'id' | 'status' | 'isActive' | 'isTrial' | 'isCwayTrial' | 'isManaged' | 'hasModulePricing' | 'autoRenewal' | 'paymentTermsDays'
>;

/**
 * The full status language for a subscription — one place so the list, the detail modal and
 * the organisation page can never disagree about what amber or purple means.
 */
export function SubscriptionStatusPills({subscription}: {subscription: SubscriptionLike | null | undefined}) {
  if (!subscription || !subscription.isActive) {
    return <Pill tone="gray">{subscription ? subscriptionStatusLabel(subscription) : 'none'}</Pill>;
  }
  // An invoiced subscription stays active through PAST_DUE — a bank transfer that has not landed
  // yet, not a declined card — so it gets amber and the invoice's own word rather than green.
  const overdueInvoice = isInvoiced(subscription) && subscription.status.toUpperCase() === PAST_DUE_STATUS;
  const terms = paymentTermsLabel(subscription.paymentTermsDays);
  return (
    <>
      {subscription.isTrial ? (
        <Pill tone="amber">{isCwayTrial(subscription) ? 'Cway trial' : 'Stripe trial'}</Pill>
      ) : (
        <Pill
          tone={overdueInvoice ? 'amber' : 'green'}
          title={overdueInvoice ? 'The invoice is past its due date; access continues until Stripe marks the subscription unpaid' : undefined}
        >
          {subscriptionStatusLabel(subscription)}
        </Pill>
      )}
      {terms && (
        <Pill tone="teal" title="Stripe sends the invoices; no card is charged and none can be replaced">
          {terms}
        </Pill>
      )}
      {subscription.isManaged && (
        <Pill tone="purple" title="Managed by Cway — customer self-service is locked">
          managed
        </Pill>
      )}
      {!subscription.hasModulePricing && !subscription.isTrial && (
        <Pill tone="gray" title="Pre-2026 pricing — module and seat controls do not apply">
          legacy pricing
        </Pill>
      )}
      {!subscription.autoRenewal && !subscription.isTrial && <Pill tone="red">cancels at period end</Pill>}
    </>
  );
}

/** Module/tier chips summarising what a subscription contains. */
export function PlanChips({
  subscription,
}: {
  subscription: Pick<ActiveSubscription, 'isActive' | 'isTrial' | 'entitlements' | 'products'> | null | undefined;
}) {
  if (!subscription || !subscription.isActive) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }

  // Entitlements are what the server actually enforces, so they gate the module chips.
  // Cway trials report the full module set (also derived for trials started before the
  // display-info change); only legacy subscriptions — and old backends — leave them empty.
  if (subscription.entitlements.length > 0) {
    const modules = subscription.entitlements.filter((e) => e.tier);
    // Seats and overage units are products, not modules, so they never appear in entitlements:
    // the seat line is the one with a seatType, an overage line the one with an addon.
    const overages = subscription.products.filter((p) => p.addon);
    // A tier (or a trial) that includes every user has no project seat block to add purchased
    // seats to. It says nothing about Lightbox seats, which no tier includes.
    const unlimitedSeats = subscription.entitlements.some((e) => e.includedUsers === UNLIMITED);
    const seatLines = subscription.products.filter((p) => p.seatType && p.quantity > 0 && !(unlimitedSeats && p.seatType === DEFAULT_SEAT_TYPE));
    return (
      <div className="flex flex-wrap gap-1.5">
        {modules.map((entitlement) => (
          <Pill key={entitlement.module} tone="blue" square>
            {MODULE_LABELS[entitlement.module]}
            {entitlement.tier ? ` · ${TIER_LABELS[entitlement.tier]}` : ''}
          </Pill>
        ))}
        {overages.map((product, index) => (
          <Pill key={`${product.id}-${index}`} tone="purple" square>
            {ADDON_LABELS[product.addon!]}
            {product.quantity > 1 ? ` × ${product.quantity}` : ''}
          </Pill>
        ))}
        {unlimitedSeats && (
          <Pill tone="teal" square>
            Unlimited {seatNoun(DEFAULT_SEAT_TYPE, {plural: true})}
          </Pill>
        )}
        {seatLines.map((product, index) => (
          <Pill key={`${product.id}-${index}`} tone="teal" square>
            +{product.quantity} {seatNoun(product.seatType!, {plural: true})}
          </Pill>
        ))}
      </div>
    );
  }

  if (subscription.isTrial) {
    // A trial without entitlements means an older backend; show the snapshotted plan
    // name ("CWAY Trial") when there is one, and never the legacy tag — it isn't one.
    return (
      <span className="text-sm text-gray-600 dark:text-gray-300">
        {subscription.products.map((product) => product.name).join(', ') || 'Trial — all modules'}
      </span>
    );
  }

  return (
    <span className="text-sm text-gray-600 dark:text-gray-300">
      {subscription.products.map((product) => product.name).join(', ') || 'Legacy plan'}
      <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">(legacy)</span>
    </span>
  );
}

/**
 * Seat usage bar. -1 total means unlimited, which gets a sentence instead of a meter.
 * `seatType` names the kind being metered — the queries ask for one kind at a time.
 */
export function SeatMeter({seatUsage, seatType = DEFAULT_SEAT_TYPE}: {seatUsage: SeatUsage; seatType?: SeatType}) {
  const seats = seatNoun(seatType, {plural: true});
  if (seatUsage.totalSeats === UNLIMITED) {
    return (
      <p className="text-sm text-gray-700 dark:text-gray-300">
        <span className="font-semibold">{seatUsage.usedSeats}</span> {seats} in use — unlimited {seats} on this plan
      </p>
    );
  }
  const percentUsed = seatUsage.totalSeats > 0 ? (seatUsage.usedSeats / seatUsage.totalSeats) * 100 : 0;
  return (
    <>
      <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
        <span>
          <span className="font-semibold">{seatUsage.usedSeats}</span> of <span className="font-semibold">{seatUsage.totalSeats}</span>{' '}
          {seats} used
        </span>
        <span>{seatUsage.seatsLeft} left</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${seatUsage.seatsLeft === 0 ? 'bg-red-500' : 'bg-blue-500'}`}
          style={{width: `${Math.min(100, percentUsed)}%`}}
        />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {seatType === LIGHTBOX_SEAT_TYPE
          ? `${seatUsage.purchasedSeats} purchased — no tier includes any; Cway sales sells them on an offer or in Stripe`
          : `${formatLimit(seatUsage.includedSeats)} included in tier + ${seatUsage.purchasedSeats} purchased`}
      </p>
    </>
  );
}
